'use client';
import { type PropsWithChildren, useEffect, useState } from 'react';
import JsCookies from 'js-cookie';
import { SuperTokensWrapper as Wrapper } from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';

import { useTranslation } from '@cms/i18n/client';
import { type ContextData } from '@common/defaults/ContextData';
import { type UserContext } from '@common/defaults/UserContext';
import { logger } from '@common/logger';
import { initSupertokens } from '@common/supertokens/initSupertokens';
import { SupertokensStatus } from '@common/supertokens/SupertokensStatus';
import { Duration } from '@common/types/Duration';
import { Route } from '@common/types/Route';
import { formatRoute } from '@common/utils/formatRoute';

interface Props extends PropsWithChildren {
    contextData: ContextData;
    userContext: UserContext;
    autoRefresh?: boolean;
    isDisabled?: boolean;
}

function getAutoRefreshMargin(env: Record<string, string | undefined>): number {
    const autoRefreshMarginEnv = Number(env.NEXT_PUBLIC_SUPERTOKENS_AUTO_REFRESH_MARGIN);
    return autoRefreshMarginEnv ? +autoRefreshMarginEnv : 5 * Duration.minute;
}

/**
 * Calculate the auto-refresh delay based on the expiration timestamp of the `sFrontToken` and the current timestamp. Subtract a margin based on the NEXT_PUBLIC_SUPERTOKENS_AUTO_REFRESH_MARGIN environment variable.
 */
function getAutoRefreshDelay(env: Record<string, string | undefined>): number | undefined {
    const sFrontToken = JsCookies.get('sFrontToken');
    if (!sFrontToken) {
        logger.warn('[getAutoRefreshDelay] Could not get sFrontToken cookie.');
        return;
    }
    const frontToken = JSON.parse(Buffer.from(sFrontToken, 'base64').toString('utf8'));
    const issuedAt = frontToken.up.iat;
    const expiresAt = frontToken.up.exp;
    if (!issuedAt || !expiresAt) {
        logger.warn("[getAutoRefreshDelay] Could not get 'up.iat' or 'up.exp' from sFrontToken cookie.");
        return;
    }
    const iatMs = issuedAt * 1000;
    const expMs = expiresAt * 1000;
    const now = Date.now();
    const delay = expMs - now;
    const autoRefreshMargin = getAutoRefreshMargin(env);
    // The margin cannot be larger than half the TTL of the access token.
    // This means you don't need to change the SUPERTOKENS_AUTO_REFRESH_MARGIN environment variable when lowering the access token TTL (e.g. for testing purposes).
    const margin = Math.min((expMs - iatMs) / 2, autoRefreshMargin);
    return Math.max(0, delay - margin);
}

export const SupertokensWrapper = ({
    contextData,
    userContext,
    children,
    autoRefresh = false,
    isDisabled = false,
}: Props) => {
    const __url = useTranslation('url').t;
    const { context } = contextData;

    // See https://supertokens.com/docs/thirdpartyemailpassword/nextjs/session-verification/in-ssr#2-doing-manual-refresh-on-the-frontend
    const [needsToRefresh, setNeedsToRefresh] = useState<boolean>(
        !!userContext && userContext.supertokens?.status === SupertokensStatus.NEEDS_REFRESH,
    );

    if (!isDisabled) initSupertokens(contextData);

    useEffect(() => {
        if (!needsToRefresh || isDisabled) {
            return;
        }

        (async () => {
            if (await Session.attemptRefreshingSession()) {
                logger.debug('Successfully refreshed access token.');
                // After session refreshing, we reload the page.
                // This will send the new access token to the server, and then getServerSideProps will succeed.
                window.location.reload();
            } else {
                logger.debug('Failed to refresh access token.');
                setNeedsToRefresh(false);
            }
        })();
    }, [needsToRefresh]);

    useEffect(() => {
        if (!autoRefresh || isDisabled) {
            return;
        }

        let timeoutID: NodeJS.Timeout | undefined;

        const doAutoRefresh = async (attemptRefresh: boolean): Promise<void> => {
            if (attemptRefresh) {
                if (!(await Session.attemptRefreshingSession())) {
                    const signin = formatRoute({
                        route: Route.Signin,
                        absolute: true,
                        context,
                        contextData,
                        __url,
                    });
                    window.location.replace(signin);
                    return;
                }
            }

            const delay = getAutoRefreshDelay(contextData.env);

            // Do not auto-refresh if the delay could not be calculated. This safeguards against infinite refresh attempts.
            if (delay === undefined) {
                return;
            }

            timeoutID = setTimeout(() => doAutoRefresh(true), delay);
        };

        doAutoRefresh(false);

        return () => clearTimeout(timeoutID);
    }, [autoRefresh]);

    return isDisabled ? <>{children}</> : <Wrapper>{children}</Wrapper>;
};
